import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router';
import { companiesWithParticipant } from '../data/db.js';
import { companyPath } from '../data/relations.js';
import { relatedCompanies, nodeTree, isOwnership, WALK_LIMITS } from '../data/network.js';
import { ErrorAlert, StatusPill } from './ui.jsx';

const IDLE = { running: false, progress: null, result: null, error: null };

const STOPPED = {
  companies: `The search stops at ${WALK_LIMITS.maxCompanies} companies.`,
  queries: `The search stops after ${WALK_LIMITS.maxQueries} queries. There are more companies below the last ones.`,
  aborted: 'You stopped the search. The list is not complete.',
};

function Row({ node }) {
  return (
    <div className="rel-row">
      <div className="who">
        <span className="kind" title="Company" aria-hidden="true">■</span>
        <Link to={companyPath(node)}>{node.name || node.enhedsNummer}</Link>
      </div>
      <span className="roles">{node.roles.join(' · ')}</span>
      {!node.active && <span className="pill plain">Former</span>}
      {node.share !== null && <span className="pill good plain">{String(node.share).replace('.', ',')} %</span>}
      {node.wasOwner && <span className="pill plain">Former owner</span>}
      {node.status && <StatusPill status={node.status} />}
    </div>
  );
}

function Tree({ items }) {
  return (
    <ul className="rel-tree">
      {items.map(({ node, children }) => (
        <li key={node.enhedsNummer}>
          <Row node={node} />
          {children.length > 0 && <Tree items={children} />}
        </li>
      ))}
    </ul>
  );
}

/**
 * A button that finds all companies below a person or a company (data/network.js).
 * The queries run only after a click, never when the page loads.
 */
export default function RelatedCompanies({ enhedsNummer }) {
  const [state, setState] = useState(IDLE);
  // Off: the walk goes below a company only through ownership, so a board seat in a
  // fund does not fill the list with the investments of the fund.
  const [allRoles, setAllRoles] = useState(false);
  const controller = useRef(null);

  useEffect(() => {
    setState(IDLE);
    return () => controller.current?.abort();
  }, [enhedsNummer]);

  async function run() {
    controller.current?.abort();
    const current = new AbortController();
    controller.current = current;
    setState({ ...IDLE, running: true, progress: { queries: 0, companies: 0 } });
    try {
      const result = await relatedCompanies(enhedsNummer, companiesWithParticipant, {
        signal: current.signal,
        follow: allRoles ? undefined : isOwnership,
        onProgress: (progress) => { if (controller.current === current) setState((s) => ({ ...s, progress })); },
      });
      if (controller.current === current) setState({ ...IDLE, result });
    } catch (error) {
      if (controller.current === current) setState({ ...IDLE, error });
    }
  }

  const { running, progress, result, error } = state;
  const activeNow = result?.nodes.filter((n) => n.status === 'NORMAL' || n.status === 'AKTIV').length ?? 0;
  const depth = result?.nodes.reduce((max, n) => Math.max(max, n.depth), 0) ?? 0;

  return (
    <section className="card">
      <div className="card-head">
        <h2>All related companies</h2>
        {result && <span className="count">{result.nodes.length} companies · {activeNow} active · {depth} levels</span>}
      </div>
      <div className="card-body">
        <p className="muted">
          The companies that this one takes part in, the companies that those companies take part in, and so on.
          {allRoles
            ? ' The search goes below each company.'
            : ' The search goes below a company only when the relation is ownership (a share, Ejer, Reel ejer, Stiftere or a former owner).'}
        </p>
        <div className="related-actions">
          {!running && (
            <button type="button" className="btn" onClick={run}>
              {result ? 'Search again' : 'Find all related companies'}
            </button>
          )}
          <label className="check">
            <input type="checkbox" checked={allRoles} disabled={running} onChange={(event) => setAllRoles(event.target.checked)} />
            {' '}Follow all roles
          </label>
          {running && (
            <>
              <button type="button" className="btn" onClick={() => controller.current?.abort()}>Stop</button>
              <span className="muted">Searching… {progress.queries} queries, {progress.companies} companies</span>
            </>
          )}
        </div>
        <ErrorAlert error={error} />
        {result && result.nodes.length === 0 && <p className="muted">No company is found.</p>}
        {result && result.nodes.length > 0 && <Tree items={nodeTree(result.nodes, enhedsNummer)} />}
        {result?.stopped && <p className="muted">{STOPPED[result.stopped]}</p>}
      </div>
    </section>
  );
}
